import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DownloadSimple, CheckCircle, Clock, Plus, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = { event_id: "", ticket_type_id: "", attendee_name: "", buyer_phone: "", buyer_email: "" };

export default function AdminAttendance() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editToken, setEditToken] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  useEffect(() => { api.get("/admin/events").then((r) => setEvents(r.data)); }, []);

  const load = () => {
    setLoading(true);
    const params = eventId !== "all" ? { event_id: eventId } : {};
    api.get("/admin/attendees", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [eventId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedTT = events.find((e) => e.id === form.event_id)?.ticket_types || [];

  const openAdd = () => { setForm({ ...emptyForm, event_id: eventId !== "all" ? eventId : "" }); setEditToken(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...emptyForm, attendee_name: r.attendee_name, buyer_phone: r.buyer_phone || "" }); setEditToken(r.token); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editToken) {
        await api.put(`/admin/tickets/${editToken}`, { attendee_name: form.attendee_name, buyer_phone: form.buyer_phone });
      } else {
        if (!form.event_id || !form.ticket_type_id || !form.attendee_name) { toast.error("Acara, jenis tiket & nama wajib diisi"); setSaving(false); return; }
        await api.post("/admin/tickets", form);
      }
      toast.success(editToken ? "Data pengunjung diperbarui" : "Pengunjung ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan"); }
    setSaving(false);
  };

  const toggleCheckin = async (r) => {
    const next = r.status === "attended" ? "issued" : "attended";
    try { await api.put(`/admin/tickets/${r.token}`, { status: next }); load(); }
    catch { toast.error("Gagal memperbarui status"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/tickets/${deleteRow.token}`); toast.success("Tiket dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
    setDeleteRow(null);
  };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const params = eventId !== "all" ? { event_id: eventId } : {};
      const res = await api.get("/admin/attendees.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "daftar-hadir.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Daftar hadir diekspor");
    } catch { toast.error("Gagal mengekspor"); }
    setDownloading(false);
  };

  const attended = rows.filter((r) => r.status === "attended").length;

  return (
    <AdminLayout title="Daftar Hadir">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger data-testid="attendance-event-select" className="w-full sm:w-72 bg-card border-white/10 rounded-none">
            <SelectValue placeholder="Semua Acara" />
          </SelectTrigger>
          <SelectContent className="bg-card border-white/10 rounded-none">
            <SelectItem value="all">Semua Acara</SelectItem>
            {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-zinc-400">Total: <b className="text-white">{rows.length}</b></span>
          <span className="text-[#00FF66]">Hadir: <b>{attended}</b></span>
          <span className="text-zinc-500">Belum: <b>{rows.length - attended}</b></span>
        </div>
        <Button data-testid="add-attendee-btn" onClick={openAdd}
          className="sm:ml-auto bg-white/10 text-white font-bold hover:bg-white hover:text-black rounded-none gap-2">
          <Plus size={18} weight="bold" /> Tambah Pengunjung
        </Button>
        <Button data-testid="export-csv-btn" onClick={exportCsv} disabled={downloading || rows.length === 0}
          className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none gap-2">
          <DownloadSimple size={18} weight="bold" /> {downloading ? "Mengekspor..." : "Ekspor CSV"}
        </Button>
      </div>

      <div className="bg-card border border-border/40 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-zinc-500">Memuat...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-zinc-600">Belum ada tiket terbit untuk pilihan ini.</div>
        ) : (
          <table className="w-full text-sm" data-testid="attendance-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-border/40 text-xs uppercase tracking-wider">
                <th className="p-3 font-semibold">Pengunjung</th>
                <th className="p-3 font-semibold">Acara</th>
                <th className="p-3 font-semibold">Jenis</th>
                <th className="p-3 font-semibold">Email</th>
                <th className="p-3 font-semibold">No HP</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.token} className="border-b border-border/20 hover:bg-white/5">
                  <td className="p-3 font-semibold text-white">{r.attendee_name}</td>
                  <td className="p-3 text-zinc-400">{r.event_name}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-2 py-0.5">{r.ticket_type_name}</span></td>
                  <td className="p-3 text-zinc-400">{r.buyer_email || "-"}</td>
                  <td className="p-3 text-zinc-400">{r.buyer_phone || "-"}</td>
                  <td className="p-3">
                    {r.status === "attended" ? (
                      <span className="text-[#00FF66] flex items-center gap-1 text-xs font-bold uppercase"><CheckCircle weight="fill" size={14} /> Hadir</span>
                    ) : (
                      <span className="text-zinc-500 flex items-center gap-1 text-xs font-bold uppercase"><Clock size={14} /> Belum</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      <Button data-testid={`toggle-checkin-${r.token}`} onClick={() => toggleCheckin(r)} variant="outline" size="icon"
                        title={r.status === "attended" ? "Batalkan check-in" : "Tandai hadir"}
                        className="h-8 w-8 border-white/20 rounded-none hover:bg-[#00FF66] hover:text-black hover:border-[#00FF66]">
                        {r.status === "attended" ? <ArrowsClockwise size={15} /> : <CheckCircle size={15} />}
                      </Button>
                      <Button data-testid={`edit-attendee-${r.token}`} onClick={() => openEdit(r)} variant="outline" size="icon" className="h-8 w-8 border-white/20 rounded-none hover:bg-primary hover:text-black">
                        <PencilSimple size={15} />
                      </Button>
                      <Button data-testid={`delete-attendee-${r.token}`} onClick={() => setDeleteRow(r)} variant="outline" size="icon" className="h-8 w-8 border-white/20 rounded-none hover:bg-secondary hover:text-white hover:border-secondary">
                        <Trash size={15} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editToken ? "Edit Pengunjung" : "Tambah Pengunjung (Walk-in)"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editToken && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">Acara</Label>
                  <Select value={form.event_id} onValueChange={(v) => setForm({ ...form, event_id: v, ticket_type_id: "" })}>
                    <SelectTrigger data-testid="attendee-event-select" className="mt-1 bg-zinc-900 border-zinc-800 rounded-none"><SelectValue placeholder="Pilih acara" /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 rounded-none">
                      {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">Jenis Tiket</Label>
                  <Select value={form.ticket_type_id} onValueChange={(v) => setForm({ ...form, ticket_type_id: v })} disabled={!form.event_id}>
                    <SelectTrigger data-testid="attendee-tt-select" className="mt-1 bg-zinc-900 border-zinc-800 rounded-none"><SelectValue placeholder="Pilih jenis tiket" /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 rounded-none">
                      {selectedTT.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Nama Pengunjung</Label>
              <Input data-testid="attendee-name-input" value={form.attendee_name} onChange={set("attendee_name")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">No HP</Label>
              <Input data-testid="attendee-phone-input" value={form.buyer_phone} onChange={set("buyer_phone")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            {!editToken && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Email (opsional)</Label>
                <Input data-testid="attendee-email-input" value={form.buyer_email} onChange={set("buyer_email")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
              </div>
            )}
            <Button data-testid="save-attendee-btn" onClick={save} disabled={saving} className="w-full bg-primary text-black font-bold hover:bg-cyan-300 rounded-none h-11">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="bg-card border-white/10 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus tiket ini?</AlertDialogTitle>
            <AlertDialogDescription>Tiket {deleteRow?.attendee_name} ({deleteRow?.ticket_type_name}) akan dihapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/20">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-attendee-btn" onClick={doDelete} className="bg-secondary text-white hover:bg-secondary/80 rounded-none">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
